import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { createSliceWithStateReset } from 'store/createSliceWithStateReset';
import { AssessmentSource } from './constants';
import { AssessmentAlertError, AssessmentsAuthMode } from './types';

interface InitialState {
  isUserVerified: boolean;
  mode: null | AssessmentsAuthMode;
  accessToken: null | string;
  expiresAt: null | string;
  tenantId: null | string;
  surveyEventId: null | string;
  assessmentsId: null | string;
  activityId: null | string;
  clientPatientId: null | string;
  firstName: null | string;
  source: AssessmentSource | null;
  isShowingAlertError: boolean;
  alertError: AssessmentAlertError;
}

const initialAlertErrorState: AssessmentAlertError = {
  message: '',
  position: 'top',
  handleDismiss: () => {},
};

// TODO: The hardcoded values will be available in verify user.
const initialState: InitialState = {
  isUserVerified: false,
  mode: null,
  accessToken: null,
  expiresAt: null,
  tenantId: null,
  surveyEventId: null,
  assessmentsId: null,
  activityId: null,
  clientPatientId: null,
  firstName: null,
  source: null,
  isShowingAlertError: false,
  alertError: initialAlertErrorState,
};

const assessmentsAuthSlice = createSliceWithStateReset({
  name: 'assessmentsAuth',
  // Using a function here ensures we always fetch the latest localStorage during initialisation.
  initialState,
  reducers: {
    setAssessmentsDetails: (
      state,
      action: PayloadAction<{
        accessToken: string | null;
        assessmentsId: string | null;
        activityId: string | null;
        clientPatientId: string | null;
        firstName: string | null;
        source?: AssessmentSource;
        expiresAt?: string | null;
        tenantId?: string | null;
        surveyEventId?: string | null;
        mode?: AssessmentsAuthMode;
      }>,
    ) => ({
      ...state,
      isUserVerified: true,
      mode: action.payload.mode ?? 'unauthenticated',
      tenantId: action.payload.tenantId ?? null,
      surveyEventId: action.payload.surveyEventId ?? null,
      accessToken: action.payload.accessToken,
      expiresAt: action.payload.expiresAt ?? null,
      assessmentsId: action.payload.assessmentsId,
      activityId: action.payload.activityId,
      clientPatientId: action.payload.clientPatientId,
      firstName: action.payload.firstName,
      source: action.payload.source ?? 'member-web',
    }),
    updateAssessmentsAccessToken: (state, action: PayloadAction<string>) => ({
      ...state,
      accessToken: action.payload,
    }),
    setAssessmentsAccessToken: (
      state,
      action: PayloadAction<{ accessToken: string; expiresAt: string }>,
    ) => ({
      ...state,
      accessToken: action.payload.accessToken,
      expiresAt: action.payload.expiresAt,
    }),
    clearAssessmentsDetails: () => initialState,
    showAlertError: (state, action: PayloadAction<AssessmentAlertError>) => ({
      ...state,
      isShowingAlertError: true,
      alertError: action.payload,
    }),
    hideAlertError: (state) => ({
      ...state,
      isShowingAlertError: false,
      alertError: initialAlertErrorState,
    }),
    clearAssessmentsAuth: () => ({ ...initialState }),
  },
});

export const {
  setAssessmentsDetails,
  updateAssessmentsAccessToken,
  setAssessmentsAccessToken,
  clearAssessmentsDetails,
  showAlertError,
  hideAlertError,
  clearAssessmentsAuth,
} = assessmentsAuthSlice.actions;

export const { reducer: assessmentsAuthReducer } = assessmentsAuthSlice;

export const selectIsAssessmentUserVerified = (state: RootState) =>
  state.assessmentsAuth.isUserVerified && state.assessmentsAuth.mode !== 'authenticated';

export const selectAssessmentsMode = (state: RootState) => state.assessmentsAuth.mode;

export const selectSurveyEventId = (state: RootState) => state.assessmentsAuth.surveyEventId;

export const selectAssessmentsTenantId = (state: RootState) => state.assessmentsAuth.tenantId;

export const selectAssessmentsId = (state: RootState) => state.assessmentsAuth.assessmentsId;

export const selectAssessmentsAccessToken = (state: RootState) => state.assessmentsAuth.accessToken;

export const selectAssessmentsAccessTokenExpiresAt = (state: RootState) =>
  state.assessmentsAuth.expiresAt;

export const selectActivityId = (state: RootState) => state.assessmentsAuth.activityId;

export const selectClientPatientId = (state: RootState) => state.assessmentsAuth.clientPatientId;

export const selectAssessmentAlertError = (state: RootState) => ({
  isShowingAlertError: state.assessmentsAuth.isShowingAlertError,
  alertError: state.assessmentsAuth.alertError,
});

export const selectAssessmentUserFirstName = (state: RootState) => state.assessmentsAuth.firstName;

export const selectAssessmentSource = (state: RootState) => state.assessmentsAuth.source;
